import { FC, useEffect, useMemo } from 'react';
import { MetricKeyEnum, useLayerStore } from '../LayerControls/layerStore';
import { create3DPlusSymbol } from '../../components/ScanItemViewer/MeshViewer/MeshLoadingHelpers';
import { Vector3 } from 'three';
import { FeaturesetValues } from '../../network';
import { ThreeDLabel } from '../3dModels/ThreeDLabel';
import { useHighlighter } from '../ui/useHighlighter';

interface ShoulderAsymmetryProps {
  featuresetValues: FeaturesetValues | undefined;
}

const shoulderAsymmetryLayerKey = MetricKeyEnum.ShoulderAsymmetry;

export const ShoulderAsymmetry: FC<ShoulderAsymmetryProps> = ({
  featuresetValues,
}) => {
  const { addLayerState, isLayerVisible } = useLayerStore();
  useHighlighter({ layerKey: shoulderAsymmetryLayerKey });

  const result = useMemo(() => {
    try {
      if (!featuresetValues) {
        return null;
      }

      const shoulder1 = featuresetValues.mesh_metadata?.shoulder1_coordinates;
      const shoulder2 = featuresetValues.mesh_metadata?.shoulder2_coordinates;
      const shoulderAngle = featuresetValues.shoulder_asymmetry;

      if (!shoulder1 || !shoulder2) {
        throw new Error(
          'Shoulder asymmetry requires both shoulder coordinates'
        );
      }

      const shoulder1Plus = create3DPlusSymbol(new Vector3(...shoulder1));

      const shoulder2Plus = create3DPlusSymbol(new Vector3(...shoulder2));

      return {
        shoulder1Plus,
        shoulder2Plus,
        labelPosition: shoulder2,
        label: `${shoulderAngle.toFixed(2)}°`,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [featuresetValues]);

  useEffect(() => {
    if (!result) {
      return;
    }
    addLayerState(shoulderAsymmetryLayerKey, {
      key: shoulderAsymmetryLayerKey,
      visible: false,
    });
  }, [addLayerState, result]);

  const showLayer = isLayerVisible(shoulderAsymmetryLayerKey);

  if (!result) {
    return null;
  }

  return (
    <group name={shoulderAsymmetryLayerKey} visible={showLayer}>
      <primitive object={result.shoulder1Plus} />
      <primitive object={result.shoulder2Plus} />
      <ThreeDLabel
        visible={showLayer}
        position={result.labelPosition}
        label={result.label}
        plane='coronal'
        divPosition={{
          bottom: 10,
          left: 10,
        }}
        layerKey={shoulderAsymmetryLayerKey}
      />
    </group>
  );
};
